import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  OmschrijvingEnBedrag
} from '@snelstart-ventures/fiscaal/domain-logic';
import {filter, Observable, shareReplay, take} from 'rxjs';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type OmschrijvingBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-verrekenbare-verliezen',
  templateUrl: './verrekenbare-verliezen.component.html',
  styleUrls: ['./verrekenbare-verliezen.component.scss'],
  imports: [
    SharedUiButtonModule,
    SharedUiDetailsModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class VerrekenbareVerliezenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public isPartner = false;
  public isDefAangifte = false;
  public optionsJarenBox1: SelectFormControlItem<string>[] = [];
  public optionsJarenBox2: SelectFormControlItem<string>[] = [];

  public formGroup = new FormGroup({
    onverrekendeVerliezenBox1: new FormArray<OmschrijvingBedragFormGroup>([]),
    onverrekendeVerliezenBox2: new FormArray<OmschrijvingBedragFormGroup>([]),
    belastingkortingBox2: new FormArray<OmschrijvingBedragFormGroup>([]),
    onverrekendeBuitenlandseBronheffing: new FormArray<OmschrijvingBedragFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        this.isDefAangifte = entity.taxType === 'DA';
        this.generateYearOptions(entity.year!);
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.dataService.calculateLatestIncomeTaxReturn().subscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(data: any): void {
    this.formGroup.reset({}, { emitEvent: false }); // Prevent reset from emitting events
    this.updateForm(data.aangifte?.verliescompensatie?.onverrekendeVerliezenBox1 || [], this.onverrekendeVerliezenBox1);
    this.updateForm(data.aangifte?.verliescompensatie?.onverrekendeVerliezenBox2 || [], this.onverrekendeVerliezenBox2);
    this.updateForm(data.aangifte?.verliescompensatie?.onverrekendeBuitenlandseBronheffing || [], this.onverrekendeBuitenlandseBronheffing);
    this.updateForm(data.aangifte?.verliescompensatie?.belastingkortingBox2 || [], this.belastingkortingBox2);
  }

  private generateYearOptions(year: number) {
    this.optionsJarenBox1 = this.createYearOptions(year, 9); // Box 1: 6 jaar carry-forward
    this.optionsJarenBox2 = this.createYearOptions(year, 6); // Box 2: 9 jaar carry-forward
  }

  private createYearOptions(aangifteJaar: number, yearsBack: number): SelectFormControlItem<string>[] {
    const options: SelectFormControlItem<string>[] = [];
    const startYear = aangifteJaar - yearsBack;
    const endYear = aangifteJaar - 1;

    for (let year = endYear; year >= startYear; year--) {
      options.push({
        value: `Jaar ${year}`,
        label: `Jaar ${year}`
      });
    }

    return options;
  }

  get onverrekendeVerliezenBox1(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.onverrekendeVerliezenBox1;
  }

  addRijVerliesBox1(): void {
    this.onverrekendeVerliezenBox1.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRijVerliesBox1(index: number): void {
    this.onverrekendeVerliezenBox1.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get onverrekendeVerliezenBox2(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.onverrekendeVerliezenBox2;
  }

  addRijVerliesBox2(): void {
    this.onverrekendeVerliezenBox2.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRijVerliesBox2(index: number): void {
    this.onverrekendeVerliezenBox2.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get belastingkortingBox2(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.belastingkortingBox2;
  }

  addRijBelastingkortingBox2(): void {
    this.belastingkortingBox2.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRijBelastingkortingBox2(index: number): void {
    this.belastingkortingBox2.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  get onverrekendeBuitenlandseBronheffing(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.onverrekendeBuitenlandseBronheffing;
  }

  addRijOnverrekendeBuitenlandseBronheffing(): void {
    this.onverrekendeBuitenlandseBronheffing.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRijOnverrekendeBuitenlandseBronheffing(index: number): void {
    this.onverrekendeBuitenlandseBronheffing.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(item?: OmschrijvingEnBedrag): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
    });
  }

  private updateForm(data : OmschrijvingEnBedrag[], array : FormArray) {
    let lijst = data || [];
    const formArray = array;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const box1: OmschrijvingEnBedrag[] = this.onverrekendeVerliezenBox1.getRawValue()
      .filter(item => item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
      }));

    const box2: OmschrijvingEnBedrag[] = this.onverrekendeVerliezenBox2.getRawValue()
      .filter(item => item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
      }));

    const bronheffing: OmschrijvingEnBedrag[] = this.onverrekendeBuitenlandseBronheffing.getRawValue()
      .filter(item => item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
      }));

    const korting: OmschrijvingEnBedrag[] = this.belastingkortingBox2.getRawValue()
      .filter(item => item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
      }));

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: Aangifte) => ({
        ...aangifte,
        verliescompensatie: {
          ...aangifte.verliescompensatie,
          onverrekendeVerliezenBox1: box1.length > 0 ? box1 : undefined,
          onverrekendeVerliezenBox2: box2.length > 0 ? box2 : undefined,
          onverrekendeBuitenlandseBronheffing: bronheffing.length > 0 ? bronheffing : undefined,
          belastingkortingBox2: korting.length > 0 ? korting : undefined,
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }

}


